import { useEffect, useState, useMemo } from 'react';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useQueryStateContext } from '../../../../contexts';
import { useVisualComponentsContext } from '../../../../contexts/visualComponents';
import { useCampaignOptions } from './useCampaignOptions';
import { ValidatedMultiStringValueInputProps } from '../types';
import { useCombineFetchedOptions } from '../../../hooks/useCombineFetchedOptions';

export const MultiCampaignSelect = ({
  onChange,
  value,
  isInvalid,
  isOpenByDefault,
  hasWarning,
  dataType,
  field,
  fieldType,
  comparator,
  optionsLimit,
}: ValidatedMultiStringValueInputProps) => {
  const { t } = useTranslation();
  const [debouncedSearch, search, setSearch] = useDebouncedState('', 500);
  const {
    ConditionValueMultiStringSelect: VisualComponent,
    ConditionValueLoadingSelect: LoadingComponent,
  } = useVisualComponentsContext();
  const { isReadOnly } = useQueryStateContext();
  const [didInitialFetch, setDidInitialFetch] = useState(false);
  const [selectedCount, setSelectedCount] = useState(value?.length || 0);
  const {
    options,
    selectedOptions,
    didError,
    campaignsCount,
    resetCampaignPages,
    isSuggestedCampaignsLoading,
    isSelectedCampaignsLoading,
    suggestedCampaignPage,
    selectedCampaignPage,
    onFetchMoreCampaigns,
  } = useCampaignOptions({
    search: debouncedSearch,
    values: value,
    isRecurring: field === 'recurringCampaignId',
  });

  const { combinedOptions: combinedSuggestedOptions, resetCombinedOptions: resetSuggestedOptions } =
    useCombineFetchedOptions({
      options,
      page: suggestedCampaignPage,
      isLoading: isSuggestedCampaignsLoading,
    });

  const { combinedOptions: combinedSelectedOptions, resetCombinedOptions: resetSelectedOptions } =
    useCombineFetchedOptions({
      options: selectedOptions,
      page: selectedCampaignPage,
      isLoading: isSelectedCampaignsLoading,
    });

  const combinedOptions = useMemo(
    () => [...combinedSelectedOptions, ...combinedSuggestedOptions],
    [combinedSelectedOptions, combinedSuggestedOptions],
  );

  const isLoading = isSuggestedCampaignsLoading || isSelectedCampaignsLoading;

  const isDisabled =
    isReadOnly || isSuggestedCampaignsLoading || isSelectedCampaignsLoading || didError;

  const hasError = didError || !!isInvalid;

  const handleSetSearch = (newSearch: string) => {
    if (newSearch !== search) {
      setSearch(newSearch);
      resetSuggestedOptions();
      resetSelectedOptions();
      resetCampaignPages();
    }
  };

  const multiCampaignLabel = t(
    `v2.conditionBuilder.campaignTriggerLabel${selectedCount === 1 ? 'One' : 'Other'}`,
    {
      count: selectedCount,
    },
  );

  // Needs some time to parse combined options
  useEffect(() => {
    if (!didInitialFetch && !isLoading) {
      const combinedOptionsLength = combinedOptions.length;
      if (
        !!combinedOptionsLength ||
        (combinedOptionsLength === 0 && campaignsCount === 0) ||
        didError
      ) {
        setDidInitialFetch(true);
      }
    }
  }, [didInitialFetch, isLoading, campaignsCount, combinedOptions, didError]);

  return (
    <>
      {didInitialFetch ? (
        <VisualComponent
          isDisabled={isDisabled}
          isLoading={isLoading}
          options={combinedOptions}
          isEmpty={value?.length === 0}
          optionsLimit={optionsLimit}
          hasError={hasError}
          hasWarning={hasWarning}
          isOpenByDefault={isOpenByDefault}
          onChange={onChange}
          onSelectChange={(newSelected) => setSelectedCount(newSelected?.length)}
          value={value}
          dataType={dataType}
          field={field}
          fieldType={fieldType}
          comparator={comparator}
          placeholder={t('v2.conditionBuilder.multiCampaignPlaceholder')}
          search={search}
          setSearch={handleSetSearch}
          triggerContent={multiCampaignLabel}
          isVirtualized
          shouldHideDisplayValues
          onFetch={onFetchMoreCampaigns}
        />
      ) : (
        <LoadingComponent />
      )}
    </>
  );
};
